// Load decision for MetricRegistryProvider, extracted so it can be tested
// without a JSX/DOM runner (this repo has none — pure .mjs logic modules with
// `node --test` alongside is the established pattern).
//
// The registry endpoints (GET /api/data/registry/metrics, /aliases) require a
// session, but the provider wraps the entire app including the public landing
// page. The decision is therefore not "fetch on mount" but "fetch when there
// is a session", and it has to be re-evaluated when that changes: the provider
// sits above the router and does not remount on a client-side login.

/**
 * @param {{ isAuthenticated: boolean }} session
 * @returns {{ fetch: boolean, loading: boolean, resolveEmpty: boolean }}
 *   fetch        — issue the registry requests
 *   loading      — value to put on the context while the decision settles
 *   resolveEmpty — settle straight to an empty (fallback-everything) registry
 */
export function planRegistryLoad({ isAuthenticated }) {
  if (!isAuthenticated) {
    // Same end state the catch branch produces on a failed fetch: loaded,
    // empty, every accessor falls back to the bundled defaults. Reaching it
    // without a request that is guaranteed to 401 is the only difference.
    return { fetch: false, loading: false, resolveEmpty: true }
  }

  return { fetch: true, loading: true, resolveEmpty: false }
}

/**
 * Classify a registry fetch rejection for the console.
 *
 * A refusal is not an outage, and downstream they are indistinguishable: both
 * settle to the same empty registry, so a session that has lost view_data runs
 * entirely on bundled defaults and looks exactly like a registry with nothing
 * in it. The log line is the only place the two can be told apart.
 *
 * @param {string} endpoint
 * @param {unknown} err
 * @returns {{ refused: boolean, status: number|null, message: string }}
 */
export function describeRegistryFailure(endpoint, err) {
  const status = err?.response?.status ?? err?.status ?? null
  const refused = status === 403
  return {
    refused,
    status,
    message: refused
      ? `[MetricRegistryContext] ${endpoint} refused (403) — this session lacks view_data. Not an outage: every accessor is now on the bundled defaults (data/metricRegistry.js, data/palettes.js), which reads identically to an empty registry.`
      : `[MetricRegistryContext] ${endpoint} unreachable${status ? ` (${status})` : ''} — falling back to bundled defaults (data/metricRegistry.js, data/palettes.js) for every accessor.`,
  }
}
